import * as Select from '@radix-ui/react-select';
import type { KeyboardEvent } from 'react';
import { useCallback } from 'react';

import { DropdownItemLabel } from '~/chords/DropdownSingleSelect/components/DropdownItemLabel';
import type {
  InternalDropdownOption,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/chords/DropdownSingleSelect/types';
import { Icon } from '~/chords/Icon';
import { cn } from '~/lib/cn';

interface DropdownItemProps extends RadixUiProps {
  option: InternalDropdownOption;
  selectedOption?: InternalDropdownOption;
  onSelectOption?: (option: InternalDropdownOption) => void;
  showCheckIcon?: boolean;
  searchQuery?: string;
  OptionTemplate?: OptionTemplateComponent;
}

export function DropdownItem({
  option,
  selectedOption,
  onSelectOption,
  showCheckIcon,
  searchQuery,
  OptionTemplate,
  radix = {},
}: DropdownItemProps) {
  const handleOptionMouseUp = useCallback(() => {
    if (!option.disabled) {
      onSelectOption?.(option);
    }
  }, [onSelectOption, option]);

  const handleOptionKeyUp = useCallback(
    (event: KeyboardEvent) => {
      // Select the option when the Enter or Space key is pressed.
      if (!option.disabled && ['Enter', ' '].includes(event.key)) {
        onSelectOption?.(option);
      }
    },
    [onSelectOption, option],
  );

  if (!option.isVisible) {
    return null;
  }

  let OptionLabel = OptionTemplate || DropdownItemLabel;

  return (
    <Select.Item
      {...radix.selectItem}
      value={option.value}
      disabled={option.disabled}
      className={cn(
        'flex min-h-[36px] items-center gap-xs px-md transition-colors duration-300',
        option.disabled
          ? 'cursor-default'
          : 'cursor-pointer text-default hover:bg-emphasis-strong focus:outline-none focus:bg-emphasis-strong',
        'data-[state="checked"]:bg-primary-default data-[state="checked"]:focus:bg-secondary-default',
        radix.selectItem?.className,
      )}
      style={{
        ...radix.selectItem?.style,
      }}
      onMouseUp={handleOptionMouseUp}
      onKeyUp={handleOptionKeyUp}
      asChild
      aria-selected={selectedOption?.value === option.value}
    >
      <div>
        {showCheckIcon ? (
          <div className="-ml-xs w-md">
            <Select.ItemIndicator>
              <Icon type="check" size="sm" />
            </Select.ItemIndicator>
          </div>
        ) : null}
        <Select.ItemText>
          <OptionLabel option={option} searchQuery={searchQuery} />
        </Select.ItemText>
      </div>
    </Select.Item>
  );
}

DropdownItem.displayName = 'DropdownItem';
